import React, { Ref } from "react";
import ProductReferenceResponse from "../../../api/models/ProductReferenceResponse";
import AssignDialog from "./AssignDialog";
import InternalContactDialog from "./dialogs/InternalContactDialog";
import ProductSuggestionsDialog from "./dialogs/ProductSuggestionDialog";
import EditRequestNameDialogHolder, { EditRequestNameDialogHolderRef } from "./EditRequestNameDialogHolder";
import DescriptionFormType from "./models/Description.form";
import { ProductSuggestionFormType } from "./models/ProductSuggestion.form-model";
import * as Fields from "./forms/fieldNames";
import TestRequestResponse from "../../../api/models/test-request/TestRequestResponse";
import TestRequestPurpose from "../../../api/models/test-request/TestRequestPurpose";
import PrototypeSuggestionDialog from "./dialogs/PrototypeSuggestionDialog";
import PrototypeSampleAddFormType from "../../../api/models/product/PrototypeSampleAddFormType";
import CompetitorSuggestionDialog from "./dialogs/CompetitorSuggestionDialog";
import CompetitorProduct from "../../../api/models/product/CompetitorProduct";

type CSCLaboratory = TestRequestRaw.Schemas.CSCLaboratory;

type ConfirmationOverride = {
  [K in keyof DescriptionFormType]?: boolean;
};
type TestRequestDescriptionPermissions = Pick<
  TestRequestRaw.Schemas.TestRequestPermissions,
  "description"
>["description"];
type TestRequestState = TestRequestResponse["state"];

interface CompetitorSampleAddFormType {
  product: CompetitorProduct | null;
}

interface DescriptionFormDialogsProps {
  formValues: DescriptionFormType;
  isAssignDialogOpen: boolean;
  isInternalContactDialogOpen: boolean;
  isSalesProductsDialogOpen: boolean;
  isAddPrototypeDialogOpen: boolean;
  isCompetitorDialogOpen: boolean;
  permissions: TestRequestDescriptionPermissions;
  preSave: () => Promise<TestRequestResponse | void>;
  handleAddSalesProduct: (productData: ProductSuggestionFormType) => void;
  handleEditSalesProduct: (productData: ProductSuggestionFormType) => void;
  handleEditPrototypeProduct: (productData: PrototypeSampleAddFormType) => void;
  handleAddPrototypeProduct: (productData: PrototypeSampleAddFormType) => void;
  handleAddCompetitorProduct: (productData: CompetitorSampleAddFormType) => void;
  handleEditCompetitorProduct: (productData: CompetitorSampleAddFormType) => void;
  handleSave: (
    values: DescriptionFormType,
    refresh?: boolean | undefined,
    confirmationOverride?: ConfirmationOverride,
  ) => Promise<TestRequestResponse | void>;
  resetSalesProductInitialData: () => void;
  selectedSalesProduct: ProductReferenceResponse | null;
  setIsAssignDialogOpen: React.Dispatch<React.SetStateAction<boolean>>;
  setIsInternalContactDialogOpen: React.Dispatch<React.SetStateAction<boolean>>;
  setIsSalesProductsDialogOpen: React.Dispatch<React.SetStateAction<boolean>>;
  setIsAddPrototypeDialogOpen: React.Dispatch<React.SetStateAction<boolean>>;
  setIsCompetitorDialogOpen: React.Dispatch<React.SetStateAction<boolean>>;
  refreshDescriptionTab: (testReqData: TestRequestResponse, redirect?: boolean) => Promise<void>;
  testRequestId: string;
  testRequestPurpose: TestRequestPurpose;
  testRequestState: TestRequestState;
}

function DescriptionFormDialogs(
  props: DescriptionFormDialogsProps,
  ref: Ref<EditRequestNameDialogHolderRef> | undefined,
) {
  const {
    formValues,
    handleSave,
    handleAddSalesProduct,
    handleEditSalesProduct,
    handleAddPrototypeProduct,
    handleEditPrototypeProduct,
    handleAddCompetitorProduct,
    handleEditCompetitorProduct,
    isAssignDialogOpen,
    isInternalContactDialogOpen,
    isSalesProductsDialogOpen,
    isAddPrototypeDialogOpen,
    isCompetitorDialogOpen,
    permissions,
    preSave,
    refreshDescriptionTab,
    resetSalesProductInitialData,
    selectedSalesProduct: salesProductInitialData,
    setIsAssignDialogOpen,
    setIsInternalContactDialogOpen,
    setIsSalesProductsDialogOpen,
    setIsAddPrototypeDialogOpen,
    setIsCompetitorDialogOpen,
    testRequestId,
    testRequestPurpose,
    testRequestState,
  } = props;

  const allowLabChange = !!testRequestState && testRequestState !== "FOR_APPROVAL";
  return (
    <>
      <AssignDialog
        testRequestId={testRequestId}
        currentLaboratory={allowLabChange && (formValues[Fields.CSC_LAB] as CSCLaboratory)}
        open={isAssignDialogOpen}
        onSuccess={refreshDescriptionTab}
        onClose={() => setIsAssignDialogOpen(false)}
        preSave={preSave}
        testRequestPurpose={testRequestPurpose}
      />
      <InternalContactDialog
        disabled={!permissions?.editDescription}
        initialValues={formValues}
        open={isInternalContactDialogOpen}
        onClose={() => setIsInternalContactDialogOpen(false)}
        onConfirm={(newValues) => {
          handleSave(newValues, true, {
            furtherInternalContacts: true,
          });
          setIsInternalContactDialogOpen(false);
        }}
      />
      <ProductSuggestionsDialog
        readOnly={!permissions?.editDescription}
        open={isSalesProductsDialogOpen}
        onClose={() => setIsSalesProductsDialogOpen(false)}
        onConfirm={salesProductInitialData ? handleEditSalesProduct : handleAddSalesProduct}
        preset={salesProductInitialData}
        resetSalesProductInitialData={resetSalesProductInitialData}
      />
      <PrototypeSuggestionDialog
        open={isAddPrototypeDialogOpen}
        onClose={() => {
          setIsAddPrototypeDialogOpen(false);
        }}
        onConfirm={salesProductInitialData ? handleEditPrototypeProduct : handleAddPrototypeProduct}
        preset={salesProductInitialData}
        resetSalesProductInitialData={resetSalesProductInitialData}
      />
      <CompetitorSuggestionDialog
        preset={salesProductInitialData}
        open={isCompetitorDialogOpen}
        onClose={() => {
          setIsCompetitorDialogOpen(false);
        }}
        onConfirm={salesProductInitialData ? handleEditCompetitorProduct : handleAddCompetitorProduct}
        resetSalesProductInitialData={resetSalesProductInitialData}
      />
      <EditRequestNameDialogHolder ref={ref} />
    </>
  );
}

export default React.forwardRef(DescriptionFormDialogs);
